/**
 * ★ OS DOIS LIMITES da sessão paga — [spec-008 T035], US-6. Sétimo irmão de
 * `transporte-tempo-real.js`: aqui não há `RTCPeerConnection`, nem `apiVoz`,
 * nem DOM de tela. Entram os tiques de consumo do provedor e a visibilidade da
 * aba, saem um AVISO e, se ninguém reagir, o pedido de encerrar.
 *
 * | limite | fonte | por quê |
 * |---|---|---|
 * | teto de DURAÇÃO | o ledger NATIVO (`session.usage.updated → usage.seconds`) | a sessão é paga por segundo e o servidor aceita o teto que ele mesmo declarou na abertura (`teto_segundos`) |
 * | perda de FOCO prolongada | `document.visibilityState` | microfone aberto e canal pago com ninguém olhando a tela é o pior desfecho de custo |
 *
 * ## ★ O relógio do teto é o do PROVEDOR — não se inventa um paralelo
 *
 * Medido no G-14 (30 sessões): `usage.seconds` chega pelo data channel em
 * **cadência fixa de 15,0 s**, o 1º tique em `t≈20,5 s` já valendo 15,0 — ou
 * seja, **o contador começa ~5,5 s ANTES do `session.started`**, no handshake.
 * O número final vem no `session.closed`. E o relógio local NÃO reconstrói o
 * cobrado: nas 29 sessões com artefato, relógio 1.150 s × cobrado 872 s, com a
 * diferença por sessão entre 7,7 s e 16,0 s — **reconstruir a partir do relógio
 * erra para MENOS, sempre**.
 *
 * Duas consequências que decidem desenho aqui:
 *
 * 1. **O ledger vem ADIANTADO do nosso relógio**, então cortar por ele corta um
 *    pouco CEDO — que é o lado certo de errar num recurso pago.
 * 2. **Sessão curta (< ~20 s) não emite tique nenhum.** O vigia do teto
 *    simplesmente não dispara nessas, e está correto: nenhuma delas chega perto
 *    de um teto de 900 s.
 *
 * ## ★ Por que o aviso vem 60 s antes, e não 5
 *
 * Com tique de 15 s, o aviso e o corte precisam cair em tiques DIFERENTES —
 * senão "avisar antes" seria a mesma mensagem no mesmo instante. 60 s são
 * QUATRO tiques: o aviso chega no pior caso ~45 s antes do corte, tempo real
 * para o consultor fechar o raciocínio. O número é DERIVADO da cadência medida,
 * não escolhido no olho.
 *
 * ⚠️ **Os dois prazos de FOCO são POLÍTICA, não medição** — não há nada no
 * G-14 sobre atenção do consultor. Estão nomeados e juntos para serem
 * revisados de uma vez, e é a única coisa deste arquivo que não sai de um fato
 * observado.
 */

/** Motivos NOMEADOS dos dois limites — viajam no aviso e no encerramento. */
export const MOTIVO_TETO_DE_DURACAO = 'teto_de_duracao'
export const MOTIVO_FOCO_PERDIDO = 'foco_perdido'

/**
 * Quanto antes do teto o consultor é avisado. **Derivado da cadência medida**
 * (15,0 s por tique, G-14): são 4 tiques, o mínimo que garante o aviso num
 * tique ANTERIOR ao do corte com folga para reagir.
 */
export const SEGUNDOS_DE_AVISO_ANTES_DO_TETO = 60

/**
 * ⚠️ POLÍTICA, não medição. Aba escondida por 2 min avisa; por 3 min, encerra.
 * O que sustenta a ordem de grandeza: o canal é pago por segundo e o microfone
 * segue aberto — mas trocar de aba para conferir um número é uso legítimo, e
 * cortar em 30 s seria hostil.
 */
export const MS_AVISO_DE_FOCO_PERDIDO = 120000
export const MS_TETO_DE_FOCO_PERDIDO = 180000

const EVENTO_DE_VISIBILIDADE = 'visibilitychange'

const _documentoPadrao = () =>
  typeof globalThis.document !== 'undefined' ? globalThis.document : null

/**
 * O vigia. Uma instância por sessão — o teto vem da abertura e o estado de
 * foco é daquela sessão; herdá-lo faria a seguinte nascer com o relógio de
 * foco da anterior já correndo.
 *
 * @param {Object} params
 * @param {(aviso: {motivo: string, segundosRestantes: number|null, cancelado: boolean}) => void} params.avisar
 *   o AVISO que vai ao consultor **antes** do corte.
 * @param {(motivo: string) => void} params.encerrar o corte. Chamado UMA vez.
 * @param {Document} [params.documento] injetável para teste; o default é o do
 *   navegador. Não há leitura de DOM de tela — só `visibilityState`.
 */
// eslint-disable-next-line max-statements
/**
 * @param {Object} params
 * @param {() => boolean} [params.consultaEmVoo] ★★★ [T112] Há uma
 *   `function_call` esperando resultado? O corte por aba escondida **não
 *   acontece** enquanto houver: uma consulta ao agente leva de 1 a 4 minutos, o
 *   teto de foco perdido são 3, e o consultor que troca de aba para conferir um
 *   número perderia exatamente a resposta que pediu. O teto de CUSTO da sessão
 *   (`tetoSegundos`) continua valendo integralmente — quem é suspenso aqui é só
 *   a política de atenção, que é política, não medição.
 */
export const criarVigiaDeLimites = ({
  avisar,
  encerrar,
  documento,
  consultaEmVoo,
}) => {
  // O estado da sessão vigiada, num objeto só: são quatro campos que só fazem
  // sentido juntos (o teto declarado na abertura e as três bandeiras de
  // uma-vez-só) e que nascem e morrem com `iniciar`/`parar`.
  const _sessao = {
    tetoSegundos: null,
    avisouDoTeto: false,
    encerrou: false,
    ligado: false,
  }
  // Os dois prazos da política de atenção, num par só — o aviso aos 2 min e o
  // corte aos 3. Juntos porque nascem, morrem e são limpos sempre juntos.
  const _foco = { aviso: null, corte: null }
  const _doc = documento || _documentoPadrao()

  const _limparTemporizadoresDeFoco = () => {
    if (_foco.aviso) clearTimeout(_foco.aviso)
    if (_foco.corte) clearTimeout(_foco.corte)
    _foco.aviso = null
    _foco.corte = null
  }

  /** O corte acontece UMA vez: teto e foco podem competir no mesmo instante. */
  const _encerrarUmaVez = (motivo) => {
    if (_sessao.encerrou) return
    _sessao.encerrou = true
    _limparTemporizadoresDeFoco()
    encerrar(motivo)
  }

  const _avisar = (motivo, segundosRestantes, cancelado) => {
    if (typeof avisar !== 'function') return
    avisar({
      motivo,
      segundosRestantes:
        typeof segundosRestantes === 'number' ? segundosRestantes : null,
      cancelado: Boolean(cancelado),
    })
  }

  // ── O teto de duração, pelo ledger do provedor ───────────────────────────

  /**
   * ★ Um tique de consumo. **É o ÚNICO relógio do teto** — não há `setInterval`
   * de parede aqui, de propósito: o número que o servidor cobra é este, e um
   * contador paralelo divergiria dele para menos (medido).
   *
   * @param {number} segundos `usage.seconds` do provedor (monotônico).
   */
  const aoAtualizarConsumo = (segundos) => {
    if (!_sessao.ligado || _sessao.encerrou) return
    if (typeof _sessao.tetoSegundos !== 'number' || !(_sessao.tetoSegundos > 0)) return
    if (typeof segundos !== 'number' || !Number.isFinite(segundos)) return
    if (segundos >= _sessao.tetoSegundos) {
      _encerrarUmaVez(MOTIVO_TETO_DE_DURACAO)
      return
    }
    const limiar = _sessao.tetoSegundos - SEGUNDOS_DE_AVISO_ANTES_DO_TETO
    if (_sessao.avisouDoTeto || segundos < limiar) return
    _sessao.avisouDoTeto = true
    // O restante é calculado sobre o LEDGER, não sobre o relógio: é o número
    // que de fato decide o corte.
    _avisar(MOTIVO_TETO_DE_DURACAO, _sessao.tetoSegundos - segundos, false)
  }

  // ── A perda de foco prolongada ───────────────────────────────────────────

  /**
   * Aqui o temporizador de parede é LEGÍTIMO, e é a única medida possível: não
   * existe ledger de atenção. Ele mede quanto tempo a aba passou escondida —
   * nunca quanto a sessão custou.
   */
  const _aoEsconder = () => {
    _limparTemporizadoresDeFoco()
    _foco.aviso = setTimeout(() => {
      _foco.aviso = null
      _avisar(
        MOTIVO_FOCO_PERDIDO,
        (MS_TETO_DE_FOCO_PERDIDO - MS_AVISO_DE_FOCO_PERDIDO) / 1000,
        false
      )
    }, MS_AVISO_DE_FOCO_PERDIDO)
    _foco.corte = setTimeout(() => {
      _foco.corte = null
      // ★★★ [T112] Consulta em voo não é aba abandonada. Cortar aqui mataria a
      // sessão que está justamente esperando a resposta que o consultor pediu.
      if (typeof consultaEmVoo === 'function' && consultaEmVoo()) return
      _encerrarUmaVez(MOTIVO_FOCO_PERDIDO)
    }, MS_TETO_DE_FOCO_PERDIDO)
  }

  /**
   * Voltou. O corte é cancelado — e, se o aviso já saiu, ele é DESFEITO: um
   * aviso de "vai encerrar" que fica na tela depois de o motivo ter passado é
   * uma afirmação falsa. É por isso que `cancelado` existe no payload.
   */
  const _aoMostrar = () => {
    const avisoJaSaiu = !_foco.aviso && Boolean(_foco.corte)
    _limparTemporizadoresDeFoco()
    if (avisoJaSaiu) _avisar(MOTIVO_FOCO_PERDIDO, null, true)
  }

  const _aoMudarVisibilidade = () => {
    if (!_sessao.ligado || _sessao.encerrou || !_doc) return
    if (_doc.visibilityState === 'hidden') {
      _aoEsconder()
      return
    }
    _aoMostrar()
  }

  /**
   * Liga o vigia. O teto vem da ABERTURA (`teto_segundos` da resposta do nosso
   * servidor) — o navegador não o escolhe; ele o obedece.
   */
  const iniciar = ({ tetoSegundos } = {}) => {
    _sessao.tetoSegundos =
      typeof tetoSegundos === 'number' && Number.isFinite(tetoSegundos)
        ? tetoSegundos
        : null
    _sessao.avisouDoTeto = false
    _sessao.encerrou = false
    _sessao.ligado = true
    if (_doc && typeof _doc.addEventListener === 'function') {
      _doc.addEventListener(EVENTO_DE_VISIBILIDADE, _aoMudarVisibilidade)
    }
    // A aba já pode estar escondida na abertura (sessão aberta por atalho e a
    // pessoa trocou de janela antes de o canal subir).
    _aoMudarVisibilidade()
  }

  /** Idempotente. Sem temporizador órfão e sem ouvinte pendurado no `document`. */
  const parar = () => {
    _sessao.ligado = false
    _limparTemporizadoresDeFoco()
    if (_doc && typeof _doc.removeEventListener === 'function') {
      _doc.removeEventListener(EVENTO_DE_VISIBILIDADE, _aoMudarVisibilidade)
    }
  }

  return { iniciar, parar, aoAtualizarConsumo }
}
